import type { SavedSearchModel } from 'ts-core';

/**
 * Map a SavedSearchModel (snake-cased filter keys mirroring the backend) to a
 * BalloonEventsQuery payload understood by the QueryProvider.
 *
 * Extracted out of InitialFilterApplier so `useApplyTagFilter` can use it
 * without a circular import (InitialFilterApplier now uses the hook).
 */
export function buildQueryFromSavedSearch(
    saved: SavedSearchModel,
    metadata: { getCategoryBySlug?: (slug: string) => { id: string } | undefined } | null
) {
    const f = saved.filters || {};
    const out: Record<string, any> = {
        forceMapClustering: Boolean(saved.forceMapClustering),
    };
    if (typeof f.textSearch === 'string' && f.textSearch.length > 0) {
        out.text = f.textSearch;
    }
    if (Array.isArray(f.tags) && f.tags.length > 0) {
        out.tags = f.tags;
    }
    if (Array.isArray(f.categories) && f.categories.length > 0) {
        const ids: string[] = [];
        for (const slugOrId of f.categories) {
            const resolved = metadata?.getCategoryBySlug?.(slugOrId);
            ids.push(resolved?.id ?? slugOrId);
        }
        out.categories = ids;
    }
    if (Array.isArray(f.region) && f.region.length > 0) {
        out.region = f.region;
    }
    if (Array.isArray(f.regionArea) && f.regionArea.length > 0) {
        out.regionArea = f.regionArea;
    }
    if (Array.isArray(f.providers) && f.providers.length > 0) {
        out.sources = f.providers;
    }
    if (typeof f.mustHaveAtLeastOneImage === 'boolean') {
        out.mustHaveAtLeastOneImage = f.mustHaveAtLeastOneImage;
    }
    if (typeof f.amount === 'number' && f.amount > 0) {
        out.amount = f.amount;
    }
    if (f.dateFrom || f.dateTo) {
        const from = f.dateFrom ? new Date(f.dateFrom) : undefined;
        const to = f.dateTo ? new Date(f.dateTo) : undefined;
        const dates: Date[] = [];
        if (from && !isNaN(from.getTime())) dates.push(from);
        if (to && !isNaN(to.getTime())) dates.push(to);
        if (dates.length > 0) out.dates = dates;
    }
    return out;
}
